import { memo, useCallback, useId, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Checkbox } from '@m/alchemy-ui/Checkbox';
import { CheckboxGroup, type CheckboxGroupProps } from '@m/alchemy-ui/CheckboxGroup';
import { Select } from '@m/alchemy-ui/Select';
import { IdeasDisplayEnum, IdeasTimespanEnum } from '../../../../common/core-api';
import { type PanelConfigPatch } from '../../common/panel-grid';
import { type PageCmsPanel } from '../../common/panel-types';
import { PanelEditorLabel, PanelEditorSection } from '../../CustomPage.styled';
import { panelEditorMessages } from './panel-editor-messages';

const messages = defineMessages({
  trending: {
    id: 'trending',
    defaultMessage: 'Trending'
  },
  latest: {
    id: 'latest',
    defaultMessage: 'Latest'
  },
  topVoted: {
    id: 'topVoted',
    defaultMessage: 'Top voted'
  },
  random: {
    id: 'random',
    defaultMessage: 'Random'
  },
  mostDiscussed: {
    id: 'mostDiscussed',
    defaultMessage: 'Most discussed'
  },
  mostDiscussedTimespan: {
    id: 'mostDiscussedTimespan',
    defaultMessage: 'Most discussed timespan'
  },
  lastNDays: {
    id: 'lastNDays',
    defaultMessage: 'Last %d days'
  },
  beginningOfTime: {
    id: 'beginningOfTime',
    defaultMessage: 'Beginning of time'
  }
});

const IDEAS_DISPLAY_ORDER = [
  IdeasDisplayEnum.trend,
  IdeasDisplayEnum.latest,
  IdeasDisplayEnum.voted,
  IdeasDisplayEnum.random,
  IdeasDisplayEnum.commented
];

const TIMESPAN_DAYS: readonly (readonly [IdeasTimespanEnum, number])[] = [
  [IdeasTimespanEnum.DAYS_7, 7],
  [IdeasTimespanEnum.DAYS_14, 14],
  [IdeasTimespanEnum.DAYS_30, 30],
  [IdeasTimespanEnum.DAYS_60, 60],
  [IdeasTimespanEnum.DAYS_90, 90]
];

type IdeasConfig = Extract<PageCmsPanel['config'], { __typename: 'IdeasPanelConfig' }>;

interface IdeasPanelEditorProps {
  readonly config: IdeasConfig;
  readonly onConfigChange: (patch: PanelConfigPatch) => void;
}

const IdeasPanelEditorComponent = ({ config, onConfigChange }: IdeasPanelEditorProps) => {
  const intl = useIntl();
  const id = useId();
  const displayLabelId = `ideas-display-label-${id}`;
  const timespanLabelId = `ideas-timespan-label-${id}`;
  const defaultLabelId = `ideas-default-label-${id}`;
  const { ideasDisplay, ideasDefault, timespan } = config;

  const labels: Record<IdeasDisplayEnum, string> = useMemo(
    () => ({
      [IdeasDisplayEnum.trend]: intl.formatMessage(messages.trending),
      [IdeasDisplayEnum.latest]: intl.formatMessage(messages.latest),
      [IdeasDisplayEnum.voted]: intl.formatMessage(messages.topVoted),
      [IdeasDisplayEnum.random]: intl.formatMessage(messages.random),
      [IdeasDisplayEnum.commented]: intl.formatMessage(messages.mostDiscussed)
    }),
    [intl]
  );

  // Re-derived in canonical order as a new array — the config arrays are frozen Apollo data.
  const checked = useMemo(() => IDEAS_DISPLAY_ORDER.filter((type) => ideasDisplay.includes(type)), [ideasDisplay]);
  const showTimespan = checked.includes(IdeasDisplayEnum.commented);
  const isDefaultEnabled = checked.length > 1;

  // Derive the displayed default from the checked options rather than trusting the stored
  // default — legacy data can hold a default that is no longer among the display options.
  const displayedDefault = checked.includes(ideasDefault) ? ideasDefault : (checked[0] ?? '');

  const timespanOptions = useMemo(
    () => [
      ...TIMESPAN_DAYS.map(([value, days]) => ({
        value: value as string,
        label: intl.formatMessage(messages.lastNDays).replace('%d', String(days))
      })),
      { value: IdeasTimespanEnum.FOREVER as string, label: intl.formatMessage(messages.beginningOfTime) }
    ],
    [intl]
  );

  const defaultOptions = useMemo(
    () => checked.map((type) => ({ value: type as string, label: labels[type] })),
    [checked, labels]
  );

  const handleDisplayChange = useCallback<NonNullable<CheckboxGroupProps['onChange']>>(
    (selection) => {
      const nextDisplay = IDEAS_DISPLAY_ORDER.filter((type) => selection.includes(type));
      onConfigChange({
        ideasDisplay: nextDisplay,
        // Keep the stored default within the checked options; when nothing is checked the
        // stored value stays as-is (the field is non-nullable) and the select shows empty.
        ...(nextDisplay.length > 0 && !nextDisplay.includes(ideasDefault) ? { ideasDefault: nextDisplay[0] } : {}),
        ...(nextDisplay.includes(IdeasDisplayEnum.commented) && !timespan
          ? { timespan: IdeasTimespanEnum.DAYS_30 }
          : {})
      });
    },
    [ideasDefault, timespan, onConfigChange]
  );

  const handleTimespanChange = useCallback(
    (option: { value: string }) => onConfigChange({ timespan: option.value as IdeasTimespanEnum }),
    [onConfigChange]
  );

  const handleDefaultChange = useCallback(
    (option: { value: string }) => onConfigChange({ ideasDefault: option.value as IdeasDisplayEnum }),
    [onConfigChange]
  );

  return (
    <>
      <PanelEditorSection>
        <PanelEditorLabel id={displayLabelId}>{intl.formatMessage(panelEditorMessages.display)}</PanelEditorLabel>
        <CheckboxGroup selection={checked} onChange={handleDisplayChange}>
          {({ isSelected, handleChange }) =>
            IDEAS_DISPLAY_ORDER.map((type) => (
              <Checkbox
                key={type}
                label={labels[type]}
                checked={isSelected(type)}
                onChange={() => handleChange(type)}
              />
            ))
          }
        </CheckboxGroup>
      </PanelEditorSection>
      <PanelEditorSection>
        <PanelEditorLabel id={defaultLabelId}>{intl.formatMessage(panelEditorMessages.default)}</PanelEditorLabel>
        <Select
          labelId={defaultLabelId}
          isFullWidth
          disabled={!isDefaultEnabled}
          value={displayedDefault}
          options={defaultOptions}
          onChange={handleDefaultChange}
        />
      </PanelEditorSection>
      {showTimespan && (
        <PanelEditorSection>
          <PanelEditorLabel id={timespanLabelId}>{intl.formatMessage(messages.mostDiscussedTimespan)}</PanelEditorLabel>
          <Select
            labelId={timespanLabelId}
            isFullWidth
            value={timespan ?? IdeasTimespanEnum.DAYS_30}
            options={timespanOptions}
            onChange={handleTimespanChange}
          />
        </PanelEditorSection>
      )}
    </>
  );
};

// Immutable config updates make prop comparison cheap: the editor re-renders only when its
// own config (or the grid callback) changes.
export const IdeasPanelEditor = memo(IdeasPanelEditorComponent);
